import classNames from "classnames";
import Button from "../../../ui/Button/Button";
import Checkbox from "../../../ui/Checkbox/Checkbox";
import Dropdown from "../../../ui/Dropdown/Dropdown";
import {
  BarChart,
  Filter,
  MinusCircle,
  PlusCircle,
  Search,
  Speedometer,
  TrendUp,
} from "../../../ui/Icon/Icon";
import Label from "../../../ui/Label/Label";
import NumberInput from "../../../ui/NumberInput/NumberInput";
import styles from "./KeywordFilterButton.module.scss";
import Textarea from "../../../ui/Textarea/Textarea";
import Select from "../../../ui/Select/Select";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { KeywordsOperator } from "../../../__generated__/gql/graphql";
import Feedback from "../../../ui/Feedback/Feedback";
import DropdownItemContext from "../../../ui/Dropdown/DropdownItemContext";

function RankFilter({
  defaultValues = { min: undefined, max: undefined },
  onSubmit,
}: {
  defaultValues: { min?: number; max?: number } | null;
  onSubmit: (data: { min?: number; max?: number } | null) => void;
}) {
  const { setIsOpen } = useContext(DropdownItemContext);

  const [min, setMin] = useState<number | undefined>(defaultValues?.min);
  const [max, setMax] = useState<number | undefined>(defaultValues?.max);

  return (
    <div className={styles.filter}>
      <div className={classNames(styles.inputs, styles.inputRow)}>
        <Label label="Min">
          <NumberInput
            placeholder="1"
            value={min?.toString()}
            onChange={(e) => setMin(parseInt(e.target.value))}
          />
        </Label>
        <Label label="Max">
          <NumberInput
            placeholder="100"
            value={max?.toString()}
            onChange={(e) => setMax(parseInt(e.target.value))}
          />
        </Label>
      </div>
      <div className={styles.buttons}>
        <Button
          variant="outline"
          disabled={
            typeof defaultValues?.min !== "number" &&
            typeof defaultValues?.max !== "number"
          }
          onClick={() => onSubmit(null)}
        >
          Remove filter
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSubmit(
              typeof min === "number" || typeof max === "number"
                ? { min, max }
                : null,
            );
            setIsOpen(false);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function SearchVolumeFilter({
  defaultValues = { min: undefined, max: undefined },
  onSubmit,
}: {
  defaultValues: { min?: number; max?: number } | null;
  onSubmit: (data: { min?: number; max?: number } | null) => void;
}) {
  const { setIsOpen } = useContext(DropdownItemContext);

  const [min, setMin] = useState<number | undefined>(defaultValues?.min);
  const [max, setMax] = useState<number | undefined>(defaultValues?.max);

  return (
    <div className={styles.filter}>
      <div className={classNames(styles.inputs, styles.inputRow)}>
        <Label label="Min">
          <NumberInput
            placeholder="0"
            value={min?.toString()}
            onChange={(e) => setMin(parseInt(e.target.value))}
          />
        </Label>
        <Label label="Max">
          <NumberInput
            placeholder="1000000"
            value={max?.toString()}
            onChange={(e) => setMax(parseInt(e.target.value))}
          />
        </Label>
      </div>
      <div className={styles.buttons}>
        <Button
          variant="outline"
          disabled={
            typeof defaultValues?.min !== "number" &&
            typeof defaultValues?.max !== "number"
          }
          onClick={() => onSubmit(null)}
        >
          Remove filter
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSubmit(
              typeof min === "number" || typeof max === "number"
                ? { min, max }
                : null,
            );
            setIsOpen(false);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function KeywordDifficultyFilter({
  defaultValues = { min: undefined, max: undefined },
  onSubmit,
}: {
  defaultValues: { min?: number; max?: number } | null;
  onSubmit: (data: { min?: number; max?: number } | null) => void;
}) {
  const { setIsOpen } = useContext(DropdownItemContext);

  const [min, setMin] = useState<number | undefined>(defaultValues?.min);
  const [max, setMax] = useState<number | undefined>(defaultValues?.max);

  return (
    <div className={styles.filter}>
      <div className={classNames(styles.inputs, styles.inputRow)}>
        <Label label="Min">
          <NumberInput
            placeholder="0"
            value={min?.toString()}
            onChange={(e) => setMin(parseInt(e.target.value))}
          />
        </Label>
        <Label label="Max">
          <NumberInput
            placeholder="100"
            value={max?.toString()}
            onChange={(e) => setMax(parseInt(e.target.value))}
          />
        </Label>
      </div>
      <div className={styles.buttons}>
        <Button
          variant="outline"
          disabled={
            typeof defaultValues?.min !== "number" &&
            typeof defaultValues?.max !== "number"
          }
          onClick={() => {
            onSubmit(null);
            setIsOpen(false);
          }}
        >
          Remove filter
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSubmit(
              typeof min === "number" || typeof max === "number"
                ? { min, max }
                : null,
            );
            setIsOpen(false);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function SearchIntentFilter({
  defaultValues,
  onSubmit,
}: {
  defaultValues: {
    informational: boolean;
    transactional: boolean;
    commercial: boolean;
    navigational: boolean;
  } | null;
  onSubmit: (
    data: {
      informational: boolean;
      transactional: boolean;
      commercial: boolean;
      navigational: boolean;
    } | null,
  ) => void;
}) {
  const { setIsOpen } = useContext(DropdownItemContext);

  const [informational, setInformational] = useState(
    defaultValues?.informational,
  );
  const [transactional, setTransactional] = useState(
    defaultValues?.transactional,
  );
  const [commercial, setCommercial] = useState(defaultValues?.commercial);
  const [navigational, setNavigational] = useState(defaultValues?.navigational);

  return (
    <div className={styles.filter}>
      <div className={classNames(styles.inputs, styles.checkboxes)}>
        <Label label="Informational" orientation="horizontal" reverse>
          <Checkbox
            checked={informational}
            onChange={() => setInformational((previous) => !previous)}
          />
        </Label>
        <Label label="Transactional" orientation="horizontal" reverse>
          <Checkbox
            checked={transactional}
            onChange={() => setTransactional((previous) => !previous)}
          />
        </Label>
        <Label label="Commercial" orientation="horizontal" reverse>
          <Checkbox
            checked={commercial}
            onChange={() => setCommercial((previous) => !previous)}
          />
        </Label>
        <Label label="Navigational" orientation="horizontal" reverse>
          <Checkbox
            checked={navigational}
            onChange={() => setNavigational((previous) => !previous)}
          />
        </Label>
      </div>
      <div className={styles.buttons}>
        <Button
          variant="outline"
          disabled={
            !defaultValues?.informational &&
            !defaultValues?.transactional &&
            !defaultValues?.commercial &&
            !defaultValues?.navigational
          }
          onClick={() => {
            onSubmit(null);
            setIsOpen(false);
          }}
        >
          Remove filter
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSubmit({
              informational: !!informational,
              transactional: !!transactional,
              commercial: !!commercial,
              navigational: !!navigational,
            });
            setIsOpen(false);
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function IncludeKeywordsFilter({
  maxValues,
  defaultValues,
  onSubmit,
}: {
  maxValues: number;
  defaultValues: {
    keywords: string;
    operator: KeywordsOperator.And | KeywordsOperator.Or;
  } | null;
  onSubmit: (
    data: {
      keywords: string;
      operator: KeywordsOperator.And | KeywordsOperator.Or;
    } | null,
  ) => void;
}) {
  const { setIsOpen } = useContext(DropdownItemContext);

  const [operator, setOperator] = useState(
    defaultValues?.operator ?? KeywordsOperator.And,
  );
  const [keywords, setKeywords] = useState(defaultValues?.keywords ?? "");
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={styles.filter}>
      <div className={styles.inputs}>
        <Label label="Keywords">
          <Textarea
            placeholder="One line per keyword"
            rows={3}
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
          />
          {error && <Feedback>{error}</Feedback>}
        </Label>
        <Label label="Operator">
          <Select
            value={operator}
            onChange={(value) => setOperator(value as KeywordsOperator)}
            data={[
              { label: "AND", value: KeywordsOperator.And },
              { label: "OR", value: KeywordsOperator.Or },
            ]}
          />
        </Label>
      </div>
      <div className={styles.buttons}>
        <Button
          variant="outline"
          disabled={!defaultValues?.keywords}
          onClick={() => {
            onSubmit(null);
            setIsOpen(false);
          }}
        >
          Remove filter
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (keywords.split("\n").length > maxValues) {
              setError(
                `You can only include or exclude up to 5 keywords at a time.`,
              );
              return;
            } else {
              onSubmit({
                keywords,
                operator,
              });
              setIsOpen(false);
            }
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function ExcludeKeywordsFilter({
  maxValues,
  defaultValues,
  onSubmit,
}: {
  maxValues: number;
  defaultValues: {
    keywords: string;
    operator: KeywordsOperator.And | KeywordsOperator.Or;
  } | null;
  onSubmit: (
    data: {
      keywords: string;
      operator: KeywordsOperator.And | KeywordsOperator.Or;
    } | null,
  ) => void;
}) {
  const { setIsOpen, isOpen } = useContext(DropdownItemContext);

  console.log(isOpen);

  const [operator, setOperator] = useState(
    defaultValues?.operator ?? KeywordsOperator.And,
  );
  const [keywords, setKeywords] = useState(defaultValues?.keywords ?? "");
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={styles.filter}>
      <div className={styles.inputs}>
        <Label label="Keywords">
          <Textarea
            placeholder="One line per keyword"
            rows={3}
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
          />
          {error && <Feedback>{error}</Feedback>}
        </Label>
        <Label label="Operator">
          <Select
            value={operator}
            onChange={(value) => setOperator(value as KeywordsOperator)}
            data={[
              { label: "AND", value: KeywordsOperator.And },
              { label: "OR", value: KeywordsOperator.Or },
            ]}
          />
        </Label>
      </div>
      <div className={styles.buttons}>
        <Button
          variant="outline"
          disabled={!defaultValues?.keywords}
          onClick={() => {
            onSubmit(null);
            setIsOpen(false);
          }}
        >
          Remove filter
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (keywords.split("\n").length > maxValues) {
              setError(
                `You can only include or exclude up to 5 keywords at a time.`,
              );
              return;
            } else {
              onSubmit({
                keywords,
                operator,
              });
              setIsOpen(false);
            }
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export type KeywordFilterState = {
  rank: {
    min?: number;
    max?: number;
  } | null;
  searchVolume: {
    min?: number;
    max?: number;
  } | null;
  keywordDifficulty: {
    min?: number;
    max?: number;
  } | null;
  searchIntent: {
    informational: boolean;
    transactional: boolean;
    commercial: boolean;
    navigational: boolean;
  } | null;
  includeKeywords: {
    keywords: string;
    operator: KeywordsOperator;
  } | null;
  excludeKeywords: {
    keywords: string;
    operator: KeywordsOperator;
  } | null;
};

function KeywordFilterButton({
  filterCount,
  filters,
  setFilters,
  clearFilters,
  includeRank,
}: {
  filterCount: number;
  filters: KeywordFilterState;
  setFilters: Dispatch<SetStateAction<KeywordFilterState>>;
  clearFilters: () => void;
  includeRank?: boolean;
}) {
  // We can only include or exclude 5 keywords at a time
  const keywordsFilterCount =
    (filters.excludeKeywords?.keywords.split("\n").length ?? 0) +
    (filters.includeKeywords?.keywords.split("\n").length ?? 0);

  return (
    <div className={styles.container}>
      <Dropdown
        leftIcon={<Filter />}
        variant={filterCount ? "secondary" : "outline"}
        items={[
          {
            items: [
              ...(includeRank
                ? [
                    {
                      label: "Rank",
                      IconComponent: TrendUp,
                      className: classNames({
                        [styles.active]: filters.searchVolume,
                      }),
                      items: [
                        <RankFilter
                          key="rank"
                          defaultValues={filters.rank}
                          onSubmit={(data) =>
                            setFilters((previousValue) => ({
                              ...previousValue,
                              rank: data,
                            }))
                          }
                        />,
                      ],
                    },
                  ]
                : []),
              {
                label: "Search volume",
                IconComponent: BarChart,
                className: classNames({
                  [styles.active]: filters.searchVolume,
                }),
                items: [
                  <SearchVolumeFilter
                    key="search-volume"
                    defaultValues={filters.searchVolume}
                    onSubmit={(data) =>
                      setFilters((previousValue) => ({
                        ...previousValue,
                        searchVolume: data,
                      }))
                    }
                  />,
                ],
              },
              {
                label: "Keyword difficulty",
                IconComponent: Speedometer,
                className: classNames({
                  [styles.active]: filters.keywordDifficulty,
                }),
                items: [
                  <KeywordDifficultyFilter
                    key="keyword-difficulty"
                    defaultValues={filters.keywordDifficulty}
                    onSubmit={(data) =>
                      setFilters((previousValue) => ({
                        ...previousValue,
                        keywordDifficulty: data,
                      }))
                    }
                  />,
                ],
              },
              {
                label: "Search intent",
                IconComponent: Search,
                className: classNames({
                  [styles.active]: filters.searchIntent,
                }),
                items: [
                  <SearchIntentFilter
                    key="search-intent"
                    defaultValues={filters.searchIntent}
                    onSubmit={(data) =>
                      setFilters((previousValue) => ({
                        ...previousValue,
                        searchIntent: data,
                      }))
                    }
                  />,
                ],
              },
              {
                label: "Include keywords",
                IconComponent: PlusCircle,
                className: classNames({
                  [styles.active]: filters.includeKeywords,
                }),
                items: [
                  <IncludeKeywordsFilter
                    key="include-keywords"
                    defaultValues={filters.includeKeywords}
                    maxValues={5 - keywordsFilterCount}
                    onSubmit={(data) =>
                      setFilters((previousValue) => ({
                        ...previousValue,
                        includeKeywords: data,
                      }))
                    }
                  />,
                ],
              },
              {
                label: "Exclude keywords",
                IconComponent: MinusCircle,
                className: classNames({
                  [styles.active]: filters.excludeKeywords,
                }),
                items: [
                  <ExcludeKeywordsFilter
                    key="exclude-keywords"
                    defaultValues={filters.excludeKeywords}
                    maxValues={5 - keywordsFilterCount}
                    onSubmit={(data) =>
                      setFilters((previousValue) => ({
                        ...previousValue,
                        excludeKeywords: data,
                      }))
                    }
                  />,
                ],
              },
            ],
          },
        ]}
      >
        {filterCount ? `Filters (${filterCount})` : "Filters"}
      </Dropdown>

      {filterCount > 0 && (
        <Button variant="link" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

export default KeywordFilterButton;
